import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  fromEventPattern,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { AgentService } from './agent.service/agent.service';
import { buildUserRolesStream } from './user-roles-stream';
import { AgentChangesService, AgentSearchService } from './agent-search';
import {
  Auth,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  inMemoryPersistence,
  Persistence,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
} from 'firebase/auth';
import { UserPermissionService } from './user-permissions.service';
import { LoggerService } from './logger.service';
import { AGENT_STATUS, Agent, AgentKeys, ChangeSourceType, DomainSignalId, LogMessageSourceTypes, LogMessageTypes, UserPermission, UserRole } from '@ag-common-lib/public-api';
import { ActivatedRoute, Router } from '@angular/router';
import { inject, NgZone } from '@angular/core';
import { UserRolesService } from '../services/user-roles.service';
import { IdentityApiService } from './identity-api.service';
import { DomainSignalsService } from './domain-signals.service';
import { AuthLogService } from './auth-log.service';
import { FirebaseApp } from 'firebase/app';
import { clearIndexedDbPersistence, Firestore, getFirestore, terminate } from 'firebase/firestore';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { NavigationTrackingService } from 'ag-common-svc/lib/services/navigation-tracking.service';
import { currentSessionValue, loadPerSession, type SessionValue } from './signed-in-session';

/** Marks a sign-out caused by the agent record failing to load, so the login page can say so. */
export const AGENT_UNAVAILABLE_REASON = 'agent-unavailable';

/**
 * Generous, because it is not a latency budget but the point at which we stop waiting.
 * The agent comes from the reporting API on Cloud Run, and a cold start alone has been
 * measured at 13.5s.
 */
const AGENT_LOAD_TIMEOUT_MS = 30_000;

export class AuthService {
  // Injected rather than taken as constructor parameters: subclasses call super()
  // positionally, so widening the signature would break them.
  private readonly agentSearchService = inject(AgentSearchService);
  private readonly agentChangesService = inject(AgentChangesService);

  private readonly unsupportedPersistenceErrorCodes = new Set([
    'auth/unsupported-persistence-type',
    'auth/web-storage-unsupported',
  ]);

  private navigationTrackingService = inject(NavigationTrackingService);
  private readonly identityApi = inject(IdentityApiService);
  private readonly domainSignals = inject(DomainSignalsService);
  isFromLogin$: Observable<boolean>;

  protected afterLogoutRedirectPath: string;
  protected homePathCommands$: Observable<string[]>;

  readonly currentUser$: Observable<User>;
  /**
   * The signed-in agent, or null when nobody is signed in or the token names no agent.
   *
   * Only ever the agent of whoever is signed in at the moment it emits: while a new
   * sign-in is loading it stays silent instead of replaying the previous state. Errors
   * when the agent could not be loaded.
   */
  readonly loggedInAgent$: Observable<Agent>;
  readonly userPermissions$: Observable<UserPermission[]>;
  readonly userRoles$: Observable<UserRole[]>;

  /**
   * @deprecated Use loggedInAgent$ observable instead
   */
  currentAgent$: BehaviorSubject<Agent> = new BehaviorSubject(undefined);

  protected auth: Auth;
  protected doBeforeLogOut: Array<(agent: Agent) => Promise<any>> = [];

  private db: Firestore;

  /** `loggedInAgent$` with its session tag and load state kept. */
  private readonly agentSession$: Observable<SessionValue<Agent | null>>;

  private currentUid = (): string | null => this.auth?.currentUser?.uid ?? null;

  constructor(
    fireBaseApp: FirebaseApp,
    protected ngZone: NgZone,
    protected router: Router,
    protected route: ActivatedRoute,
    private toster: ToastrService,
    private agentService: AgentService,
    protected loggerService: LoggerService,
    private userPermissionService: UserPermissionService,
    private userRolesService: UserRolesService,
    private authLogService: AuthLogService,
  ) {
    this.db = getFirestore(fireBaseApp);

    this.currentUser$ = fromEventPattern(
      handler => this.auth.onAuthStateChanged(handler),
      (_handler, unsubscribe) => {
        unsubscribe();
      },
    );

    /**
     * Keyed to the Firebase user, and never replaying one sign-in's agent to a reader
     * asking about the next one.
     *
     * The previous version cached only the last agent, so the `null` emitted before
     * sign-in kept being replayed until the new agent arrived. The approved agent comes
     * from the reporting API, which can take seconds, and the route guards asked in that
     * window: they read the stale `null` as "no access" and sent a freshly signed-in
     * agent back to the login form. The second click worked only because the agent had
     * arrived by then.
     */
    const signedInUser$ = this.currentUser$.pipe(
      map(user => (user?.getIdTokenResult ? user : null)),
      tap(user => {
        if (!user) {
          this.currentAgent$.next(null);
        }
      }),
    );

    this.agentSession$ = loadPerSession(
      signedInUser$,
      user => this.loadAgent(user).pipe(tap(agent => this.currentAgent$.next(agent))),
      null,
    );
    this.loggedInAgent$ = currentSessionValue(this.agentSession$, this.currentUid);

    this.userPermissions$ = this.loggedInAgent$.pipe(
      filter(Boolean),
      mergeMap(agent => {
        return this.userPermissionService.getList(agent[BaseModelKeys.dbId]);
      }),
      shareReplay(1),
    );

    /**
     * The signed-in agent's roles, now read from the identity API instead of being
     * assembled in the browser from `agents/{id}.roles` plus a full read of the
     * `user-roles` collection.
     *
     * Re-reads whenever the user-roles signal moves, so a role granted or revoked
     * elsewhere lands in an open session rather than waiting for the next login.
     *
     * Still unfiltered by application and by `isEnable`: the CRM, portal and PWA
     * services each apply their own filter, and narrowing here would change all three
     * at once.
     */
    this.userRoles$ = buildUserRolesStream({
      agent$: this.agentSession$,
      currentUid: this.currentUid,
      signal$: this.domainSignals.getSignalVersion(DomainSignalId.userRoles),
      fetchRoles: () => this.identityApi.getMyRoles(),
      toUserRole: role => UserRolesService.toUserRole(role),
    });
    this.isFromLogin$ = this.navigationTrackingService.isFromLogin$.pipe(shareReplay(1));
  }

  /**
   * The agent for one signed-in user, or null when the token names no agent.
   *
   * Bounded, because every reader of `loggedInAgent$`, sign-in included, waits for it.
   */
  private loadAgent(user: User): Observable<Agent | null> {
    return from(user.getIdTokenResult(true)).pipe(
      mergeMap(idTokenResult => {
        const agentId = idTokenResult?.claims?.agentDbId as string;
        if (!agentId) {
          return of(null);
        }

        /**
         * Gated on the claim, not attempted and recovered from.
         *
         * An agent who is not yet Approved cannot use the projection the ordinary
         * way: the change signals that refresh a projected row sit behind the
         * approval gate, so there is nothing to subscribe to. They get a single
         * live read of their own document, which is the one read Firestore
         * rules permit regardless of status.
         *
         * It has to be LIVE, and an earlier version of this got that wrong by
         * reading once through the API's own-record route. The under-review
         * screen watches this stream `pairwise()` for the moment the status
         * turns Approved, and a one-shot read never produces a second value —
         * so approval was never noticed, the agent was never sent back through
         * sign-in, and they carried a stale "New Agent" token into the app.
         *
         * The claim cannot tell them either: it is a session claim, fixed when
         * the session is created and copied forward verbatim by every refresh.
         * Only signing in again re-stamps it. So the document is the only thing
         * that can announce the approval from inside the session, and watching
         * it is what makes the sign-out happen.
         */
        const agentStatus = (idTokenResult?.claims?.agent as { agent_status?: string })?.agent_status;
        if (agentStatus !== AGENT_STATUS.APPROVED) {
          return this.agentService.getDocument(agentId).pipe(map(doc => doc.data()));
        }

        /**
         * Only the first read decides whether the session has an agent. A later refresh
         * that fails keeps the agent as last read: failing the whole session over it
         * would send an agent who is already working back to the login form.
         */
        let loaded = false;
        return this.agentChangesService.agentChanged$(agentId).pipe(
          startWith(undefined),
          switchMap(() =>
            from(this.agentSearchService.getById(agentId)).pipe(
              tap(() => (loaded = true)),
              catchError(error => {
                if (!loaded) {
                  return throwError(() => error);
                }

                console.error('[auth] failed to refresh the signed-in agent', error);
                return EMPTY;
              }),
            ),
          ),
        );
      }),
      timeout({ first: AGENT_LOAD_TIMEOUT_MS }),
    );
  }

  protected setAuth(fireBaseApp: FirebaseApp) {
    this.auth = getAuth(fireBaseApp);

    this.auth.beforeAuthStateChanged(async user => {
      if (user) {
        return;
      }

      // A session whose agent never loaded has nothing to clean up, and must still be
      // able to sign out.
      const loggedInAgent = await firstValueFrom(this.loggedInAgent$).catch(() => null);
      if (!loggedInAgent) {
        return;
      }

      for (const action of this.doBeforeLogOut) {
        await action(loggedInAgent);
      }

      return;
    });
  }

  async signInWithEmailAndPassword(
    email: string,
    password: string,
    persistance: Persistence = browserSessionPersistence,
  ): Promise<Agent | void> {
    try {
      const userData = await this.signIn(email, password, persistance);

      if (!userData?.user?.emailVerified) {
        await this.loggerService.createLog(
          LogMessageTypes.login,
          LogMessageSourceTypes.email,
          userData?.user?.email,
          'User exists but email not verified. ',
        );

        this.onAgentEmailNotVerified();

        return;
      }

      const idTokenResult = await userData?.user?.getIdTokenResult(true);
      const agentId = idTokenResult?.claims?.agentDbId;

      if (!agentId) {
        const ec = await this.loggerService.createLog(
          LogMessageTypes.login,
          LogMessageSourceTypes.email,
          userData?.user?.email,
          'Google Record: AgentDbId is not assigned to User',
        );

        this.toster.error(
          'An Agent record matching that Email Address could not be found. Please contact Alliance Group for Assistance with this code:' +
            ec,
          'Login Error',
          { disableTimeOut: true },
        );
        await this.logOut();

        return;
      }

      const agent = idTokenResult?.claims?.agent;

      if (!agent) {
        const ec = await this.loggerService.createLog(
          LogMessageTypes.login,
          LogMessageSourceTypes.email,
          userData?.user?.email,
          `Could not find agent record for user ${userData?.user?.email}`,
        );

        this.toster.error(
          'An Agent record matching that Email Address could not be found. Please contact Alliance Group for Assistance with this code:' +
            ec,
          'Login Error',
          { disableTimeOut: true },
        );
        await this.logOut();

        return;
      }

      return agent as Agent;
    } catch (error) {
      let errorLogCode;
      switch (error?.code) {
        case 'auth/wrong-password':
          const passwordMeta = this.getPasswordMeta(password);
          errorLogCode = await this.loggerService.createLog(
            LogMessageTypes.login,
            LogMessageSourceTypes.email,
            email,
            'You have entered an incorrect password for this email address.',
            {
              ...error,
              passwordMeta,
            },
          );

          this.toster.error(
            `<br/><p>You have entered an incorrect password for this email address.</p><p>If you have forgotten your password, enter your Email Address and press the "Forgot Password" button.</p><p>If the problem continues, please contact Alliance Group for assistance with this code: <b>${errorLogCode}</b></p>`,
            'Login Error',
            { disableTimeOut: true, enableHtml: true },
          );

          return Promise.reject(new Error('Wrong password'));

        case 'auth/user-not-found':
          errorLogCode = await this.loggerService.createLog(
            LogMessageTypes.login,
            LogMessageSourceTypes.email,
            email,
            'The email address (' + email + ') is not recognized.',
            { ...error },
          );

          this.toster.error(
            'The email address (' +
              email +
              ') is not recognized. Correct the Email Address and Try again. If the problem continues, please contact Alliance Group for assistance with this code: ' +
              errorLogCode,
            'Login Error',
            { disableTimeOut: true },
          );
          return Promise.reject(new Error('User not found'));

        case 'auth/too-many-requests':
          errorLogCode = await this.loggerService.createLog(
            LogMessageTypes.login,
            LogMessageSourceTypes.email,
            email,
            'Too many failed attempts. The account is temporarily locked.',
            { ...error },
          );
          this.toster.error(
            'There have been too many failed logins to this account. Please reset your password by going to the login screen, entering your password, and pressing the "Forgot Password" button. If the problem continues, please contact Alliance Group for assistance with this code: ' +
              errorLogCode,
            'Login Error',
            { disableTimeOut: true },
          );
          break;

        default:
          errorLogCode = await this.loggerService.createLog(
            LogMessageTypes.login,
            LogMessageSourceTypes.email,
            email,
            'Unknown Error loggin in with the email address (' + email + '). Check Error details for more information',
            {
              ...error,
              errorCode: error?.code ?? null,
              errorMessage: error?.message ?? null,
            },
          );

          this.toster.error(
            'There was an Error accessing your account (' +
              (error?.code ?? 'unknown') +
              '). Please contact Alliance Group for Assistance with this code: ' +
              errorLogCode,
            'Login Error',
            { disableTimeOut: true },
          );
          return Promise.reject(new Error('Unknown Error'));
      }
    }
  }

  logUserIntoPortal = async (actionName: ChangeSourceType, data?: Partial<Agent>): Promise<void> => {
    // Not filtered for truthiness: the guard below is the intended handling for a caller
    // with no agent record, and filtering first made it unreachable — the await simply
    // never settled instead of returning.
    const agent = await firstValueFrom(this.loggedInAgent$);
    if (!agent) {
      return;
    }
    const updates = Object.assign(
      {
        [AgentKeys.login_count]: Number.isInteger(agent?.login_count) ? agent?.login_count + 1 : 1,
        [AgentKeys.last_login_date]: new Date(),
      },
      data,
    );

    if (!agent.logged_in) {
      Object.assign(updates, {
        [AgentKeys.logged_in]: true,
        [AgentKeys.first_login_date]: new Date(),
      });
    }

    await Promise.all([
      this.agentService.updateAgentFields(agent?.dbId, updates, actionName),
      this.authLogService.create(),
    ]);
    return;
  };

  private getPasswordMeta = (value: string) => {
    const password = value ?? '';
    const chars = Array.from(password);
    const countBy = (predicate: (char: string) => boolean) => chars.filter(predicate).length;

    return {
      length: password.length,
      trimmedLength: password.trim().length,
      hasLeadingOrTrailingWhitespace: password !== password.trim(),
      hasWhitespace: /\s/.test(password),
      hasZeroWidthChars: /[\u200B-\u200D\uFEFF]/.test(password),
      hasNonAscii: /[^\x00-\x7F]/.test(password),
      charset: {
        lowercaseCount: countBy(char => /[a-z]/.test(char)),
        uppercaseCount: countBy(char => /[A-Z]/.test(char)),
        digitCount: countBy(char => /\d/.test(char)),
        symbolCount: countBy(char => /[^A-Za-z0-9\s]/.test(char)),
        whitespaceCount: countBy(char => /\s/.test(char)),
        nonAsciiCount: countBy(char => /[^\x00-\x7F]/.test(char)),
        zeroWidthCount: countBy(char => /[\u200B-\u200D\uFEFF]/.test(char)),
      },
    };
  };

  /**
   * Ends a session whose agent could not be loaded, and has the login page say so.
   *
   * Signs out rather than leaving the session open: the failed load is kept for the
   * session, and Firebase does not report a second sign-in by the same user, so
   * retrying inside it would fail the same way. A new session loads again.
   *
   * The reason and log code ride in the query string because `logOut` ends in a page
   * reload, which would take a toast raised here with it.
   */
  endSessionAfterAgentLoadFailure = async (error: unknown): Promise<void> => {
    const errorLogCode = await this.loggerService.createLog(
      LogMessageTypes.login,
      LogMessageSourceTypes.email,
      this.auth?.currentUser?.email ?? null,
      'Could not load the agent record for the signed-in user.',
      {
        errorName: (error as Error)?.name ?? null,
        errorMessage: (error as Error)?.message ?? null,
      },
    );

    await this.ngZone.run(() =>
      this.router.navigate([`/${this.afterLogoutRedirectPath}`], {
        queryParams: { reason: AGENT_UNAVAILABLE_REASON, code: errorLogCode || undefined },
      }),
    );
    await this.logOut(false);
  };

  navigateForward = async () => {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    if (returnUrl) {
      // navigateByUrl, not navigate([returnUrl]): the return URL is a whole URL. As a single
      // command it became one path segment, so "/reports-viewer/x?scope=rmd" came back as
      // "/reports-viewer/x%3Fscope%3Drmd" and the page lost its parameters after login.
      return this.ngZone.run(() => {
        return this.router.navigateByUrl(returnUrl);
      });
    }

    const homePathCommands = await firstValueFrom(this.homePathCommands$.pipe(map(cmd => cmd)));
    return this.ngZone.run(() => {
      return this.router.navigate(homePathCommands);
    });
  };

  async logOut(withRedirect = true) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => {
      return false;
    };
    try {
      if (withRedirect) {
        await this.router.navigate([this.afterLogoutRedirectPath]);
      }

      await signOut(this.auth);
      await terminate(this.db);
      await clearIndexedDbPersistence(this.db);

      window.location.reload();
    } catch (error) {
      console.error('Error in Auth Service.', error);
    }
  }

  registerUser = (email, password): Promise<UserCredential> => {
    return createUserWithEmailAndPassword(this.auth, email, password);
  };

  addBeforeLogOutAction = (action: (agent: Agent) => Promise<any>): (() => void) => {
    const nexActionIndex = this.doBeforeLogOut.length;

    this.doBeforeLogOut.push(action);

    return () => {
      this.doBeforeLogOut.splice(nexActionIndex, 1);
    };
  };

  private signIn(email, password, persistance: Persistence): Promise<UserCredential> {
    return this.setPersistenceWithFallback(persistance).then(() => {
      let retriesCount = 0;
      const action = () =>
        signInWithEmailAndPassword(this.auth, email, password).catch(e => {
          const errorsCodesToRetry = new Set(['auth/internal-error']);
          if (retriesCount <= 3 && errorsCodesToRetry.has(e?.code)) {
            retriesCount++;
            return action();
          }
          throw e;
        });

      return action();
    });
  }

  private async setPersistenceWithFallback(persistance: Persistence): Promise<void> {
    try {
      await setPersistence(this.auth, persistance);
    } catch (error) {
      if (!this.canRetryWithInMemoryPersistence(error, persistance)) {
        throw error;
      }

      console.warn('Falling back to in-memory Firebase auth persistence.', error);
      await setPersistence(this.auth, inMemoryPersistence);
    }
  }

  private canRetryWithInMemoryPersistence(error: unknown, persistance: Persistence): boolean {
    const errorCode =
      typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string'
        ? error.code
        : undefined;
    return persistance === browserSessionPersistence && this.unsupportedPersistenceErrorCodes.has(errorCode);
  }

  protected onAgentEmailNotVerified = async () => {};

  navigateHome = async () => {
    const homePathCommands = await firstValueFrom(this.homePathCommands$.pipe(map(cmd => cmd)));
    return this.ngZone.run(() => {
      return this.router.navigate(homePathCommands);
    });
  };

  setAfterLogoutRedirectPath(afterLogoutRedirectPath: string) {
    this.afterLogoutRedirectPath = afterLogoutRedirectPath;
  }
}
